import { useCallback, useEffect, useState } from 'react';
import type { WorkspaceListItem } from 'shared';
import { api } from '../utils/api';

/**
 * Every workspace, for the operator console's switcher.
 *
 * Mounted once by OperatorLayout so moving between companies costs nothing:
 * the whole point of the switcher is to save the trip back to the list, which
 * a fetch on every open would hand straight back.
 *
 * Archived ones are kept rather than filtered out here. The switcher has two
 * jobs — saying which company you are in, and offering the others — and only
 * the second wants a short list. Dropping the archived ones at the source
 * made the first job answer "All companies" while the operator was plainly
 * inside an archived one.
 *
 * A failure is silent on purpose. This feeds navigation, not a page — an
 * operator who cannot reach the list should still see the page they asked
 * for, with one fewer shortcut on it.
 */
export function useOperatorWorkspaces() {
  const [workspaces, setWorkspaces] = useState<WorkspaceListItem[]>([]);

  const refresh = useCallback(async () => {
    try {
      const response = await api.get<{ data: WorkspaceListItem[] }>('/api/workspaces');
      setWorkspaces(response.data);
    } catch {
      setWorkspaces([]);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { workspaces, refresh };
}
